import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../theme';

const LandingPage = () => {
  const navigate = useNavigate();
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [visibleElements, setVisibleElements] = useState({
    hero: false,
    subtext: false,
    buttons: false,
    features: false,
    howItWorks: false,
    languages: false,
    finalCTA: false,
    footer: false
  });
  
  const fullText = 'Run Code Instantly';

  // Typing animation for hero text
  useEffect(() => {
    let currentIndex = 0;
    let typingInterval;

    if (isTyping) {
      typingInterval = setInterval(() => {
        if (currentIndex <= fullText.length) {
          setDisplayedText(fullText.slice(0, currentIndex));
          currentIndex++;
        } else {
          setIsTyping(false);
          clearInterval(typingInterval);
        }
      }, 100);
    }

    return () => clearInterval(typingInterval);
  }, [isTyping]);

  // Scroll-triggered animations
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight;
      
      // Check each section's position
      const checkElementVisibility = (elementId, elementKey) => {
        const element = document.getElementById(elementId);
        if (element) {
          const elementTop = element.offsetTop;
          
          if (scrollPosition > elementTop + 100 && !visibleElements[elementKey]) {
            setVisibleElements(prev => ({ ...prev, [elementKey]: true }));
          }
        }
      };

      // Check hero section immediately
      if (!visibleElements.hero && window.scrollY < 200) {
        setVisibleElements(prev => ({ ...prev, hero: true }));
        setTimeout(() => setVisibleElements(prev => ({ ...prev, subtext: true })), 300);
        setTimeout(() => setVisibleElements(prev => ({ ...prev, buttons: true })), 600);
      }

      // Check other sections on scroll
      checkElementVisibility('features', 'features');
      checkElementVisibility('how-it-works', 'howItWorks');
      checkElementVisibility('languages', 'languages');
      checkElementVisibility('final-cta', 'finalCTA');
      checkElementVisibility('footer', 'footer');
    };

    // Initial check and scroll listener
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [visibleElements]);

  const handleStartCoding = () => {
    navigate('/editor');
  };

  return (
    <div style={{ backgroundColor: theme.colors.bg.primary }} className="min-h-screen">
      {/* Header Navigation */}
      <header 
        className="sticky top-0 z-50 border-b"
        style={{ 
          backgroundColor: theme.colors.bg.primary,
          borderColor: theme.colors.border.primary 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo and Brand */}
            <div 
              className="flex items-center space-x-3 cursor-pointer transition-transform hover:scale-105"
              onClick={() => navigate('/')}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.02)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
              }}
            >
              <div 
                className="w-8 h-8 rounded-md flex items-center justify-center"
                style={{ backgroundColor: theme.colors.accent.blue }}
              >
                <span 
                  className="text-white font-bold text-lg"
                  style={{ fontFamily: theme.typography.fontFamily.sans.join(', ') }}
                >
                  CM
                </span>
              </div>
              <h1 
                className="text-xl font-semibold"
                style={{ 
                  color: theme.colors.text.primary,
                  fontFamily: theme.typography.fontFamily.sans.join(', ')
                }}
              >
                CodeMate
              </h1>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <a 
                href="#features" 
                className="text-sm font-medium transition-colors hover:text-blue-400"
                style={{ color: theme.colors.text.secondary }}
              >
                Features
              </a>
              <a 
                href="#languages" 
                className="text-sm font-medium transition-colors hover:text-blue-400"
                style={{ color: theme.colors.text.secondary }}
              >
                Languages
              </a>
              <a 
                href="#how-it-works" 
                className="text-sm font-medium transition-colors hover:text-blue-400"
                style={{ color: theme.colors.text.secondary }}
              >
                How It Works
              </a>
            </nav>

            {/* CTA Button */}
            <button
              onClick={handleStartCoding}
              className="px-4 py-2 text-sm font-medium rounded-md transition-all transform hover:scale-105 active:scale-95"
              style={{
                backgroundColor: theme.colors.accent.blue,
                color: theme.colors.text.inverse,
                fontFamily: theme.typography.fontFamily.sans.join(', ')
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#0860ca';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = theme.colors.accent.blue;
              }}
            >
              Start Coding
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="text-center">
            <h1 
              className={`text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 transition-all duration-1000 transform ${
                visibleElements.hero ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ 
                color: theme.colors.text.primary,
                fontFamily: theme.typography.fontFamily.sans.join(', '),
                lineHeight: '1.1'
              }}
            >
              {displayedText}
              <span style={{ color: theme.colors.accent.blue }}>
                {isTyping ? '|' : '.'}
              </span>
            </h1>
            
            <p 
              className={`text-xl sm:text-2xl mb-8 max-w-3xl mx-auto transition-all duration-1000 transform ${
                visibleElements.subtext ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ 
                color: theme.colors.text.secondary,
                fontFamily: theme.typography.fontFamily.sans.join(', '),
                lineHeight: '1.6'
              }}
            >
              A fast, secure online code compiler for interviews, practice, and learning. 
              No setup required. Just write and run.
            </p>

            <div className={`flex flex-col sm:flex-row gap-4 justify-center items-center transition-all duration-1000 transform ${
              visibleElements.buttons ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}>
              <button
                onClick={handleStartCoding}
                className="px-8 py-4 text-lg font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95"
                style={{
                  backgroundColor: theme.colors.accent.green,
                  color: theme.colors.text.inverse,
                  fontFamily: theme.typography.fontFamily.sans.join(', ')
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#15803d';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = theme.colors.accent.green;
                }}
              >
                Start Coding Now
              </button>
              
              <a
                href="#features"
                className="px-8 py-4 text-lg font-semibold rounded-lg border-2 transition-all"
                style={{
                  color: theme.colors.text.primary,
                  borderColor: theme.colors.border.primary,
                  fontFamily: theme.typography.fontFamily.sans.join(', ')
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = theme.colors.accent.blue;
                  e.target.style.color = theme.colors.accent.blue;
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = theme.colors.border.primary;
                  e.target.style.color = theme.colors.text.primary;
                }}
              >
                View Features
              </a>
            </div>
          </div>

          {/* Background Pattern */}
          <div 
            className="absolute inset-0 opacity-5 pointer-events-none"
            style={{
              backgroundImage: `repeating-linear-gradient(
                45deg,
                ${theme.colors.accent.blue},
                ${theme.colors.accent.blue} 1px,
                transparent 1px,
                transparent 15px
              )`
            }}
          />
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 lg:py-24 ">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 transform ${
            visibleElements.features ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <h2 
              className="text-3xl sm:text-4xl font-bold mb-4"
              style={{ 
                color: theme.colors.text.primary,
                fontFamily: theme.typography.fontFamily.sans.join(', ')
              }}
            >
              Built for Developers
            </h2>
            <p 
              className="text-lg max-w-2xl mx-auto"
              style={{ 
                color: theme.colors.text.secondary,
                fontFamily: theme.typography.fontFamily.sans.join(', ')
              }}
            >
              Everything you need to write, test, and share code efficiently
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: '⚡',
                title: 'Lightning Fast',
                description: 'Compile and run your code in seconds with optimized execution environments'
              },
              {
                icon: '🔒',
                title: 'Secure Sandbox',
                description: 'Your code runs in isolated containers with comprehensive security measures'
              },
              {
                icon: '🎯',
                title: 'Interview Ready',
                description: 'Perfect for technical interviews and coding assessments with real-time feedback'
              },
              {
                icon: '🌐',
                title: 'Multi-Language',
                description: 'Support for C++, Java, JavaScript, and more with consistent behavior'
              },
              {
                icon: '📱',
                title: 'Responsive Design',
                description: 'Works seamlessly across desktop, tablet, and mobile devices'
              },
              {
                icon: '🔧',
                title: 'Clean Editor',
                description: 'Professional code editor with syntax highlighting and intelligent features'
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className={`p-6 rounded-lg border transition-all duration-700 transform hover:border-blue-500 hover:scale-105 ${
                  visibleElements.features 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-8'
                }`}
                style={{
                  backgroundColor: theme.colors.bg.secondary,
                  borderColor: theme.colors.border.primary,
                  transitionDelay: `${index * 100}ms`
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = theme.colors.accent.blue;
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = theme.colors.border.primary;
                }}
              >
                <div className="text-3xl mb-4">{feature.icon}</div>
                <h3 
                  className="text-xl font-semibold mb-3"
                  style={{ 
                    color: theme.colors.text.primary,
                    fontFamily: theme.typography.fontFamily.sans.join(', ')
                  }}
                >
                  {feature.title}
                </h3>
                <p 
                  className="text-sm leading-relaxed"
                  style={{ 
                    color: theme.colors.text.secondary,
                    fontFamily: theme.typography.fontFamily.sans.join(', ')
                  }}
                >
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 transform ${
            visibleElements.howItWorks ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <h2 
              className="text-3xl sm:text-4xl font-bold mb-4"
              style={{ 
                color: theme.colors.text.primary,
                fontFamily: theme.typography.fontFamily.sans.join(', ')
              }}
            >
              How It Works
            </h2>
            <p 
              className="text-lg max-w-2xl mx-auto"
              style={{ 
                color: theme.colors.text.secondary,
                fontFamily: theme.typography.fontFamily.sans.join(', ')
              }}
            >
              Get started in three simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                title: 'Choose Language',
                description: 'Select from supported programming languages'
              },
              {
                step: '2',
                title: 'Write Code',
                description: 'Use our professional editor with syntax highlighting'
              },
              {
                step: '3',
                title: 'Run & Test',
                description: 'Execute your code and see results instantly'
              }
            ].map((item, index) => (
              <div 
                key={index} 
                className={`text-center transition-all duration-700 transform ${
                  visibleElements.howItWorks 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold transition-transform hover:scale-110"
                  style={{
                    backgroundColor: theme.colors.accent.blue,
                    color: theme.colors.text.inverse
                  }}
                >
                  {item.step}
                </div>
                <h3 
                  className="text-xl font-semibold mb-3"
                  style={{ 
                    color: theme.colors.text.primary,
                    fontFamily: theme.typography.fontFamily.sans.join(', ')
                  }}
                >
                  {item.title}
                </h3>
                <p 
                  className="text-sm"
                  style={{ 
                    color: theme.colors.text.secondary,
                    fontFamily: theme.typography.fontFamily.sans.join(', ')
                  }}
                >
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported Languages */}
      <section id="languages" className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 transform ${
            visibleElements.languages ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <h2 
              className="text-3xl sm:text-4xl font-bold mb-4"
              style={{ 
                color: theme.colors.text.primary,
                fontFamily: theme.typography.fontFamily.sans.join(', ')
              }}
            >
              Supported Languages
            </h2>
            <p 
              className="text-lg max-w-2xl mx-auto"
              style={{ 
                color: theme.colors.text.secondary,
                fontFamily: theme.typography.fontFamily.sans.join(', ')
              }}
            >
              Code in the language you prefer
            </p>
          </div>

          <div className={`flex flex-wrap justify-center gap-3 transition-all duration-1000 transform ${
            visibleElements.languages ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            {['C++', 'Java', 'JavaScript', 'Python', 'C', 'Go', 'Rust', 'TypeScript'].map((lang, index) => (
              <span
                key={lang}
                className="px-4 py-2 rounded-full text-sm font-medium border transition-all duration-500 transform hover:scale-110 hover:border-blue-500"
                style={{
                  color: theme.colors.text.primary,
                  borderColor: theme.colors.border.primary,
                  backgroundColor: theme.colors.bg.secondary,
                  transitionDelay: `${index * 50}ms`
                }}
              >
                {lang}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="final-cta" className="py-20 lg:py-24">
        <div className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 transform ${
          visibleElements.finalCTA ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 
            className="text-3xl sm:text-4xl font-bold mb-6"
            style={{ 
              color: theme.colors.text.primary,
              fontFamily: theme.typography.fontFamily.sans.join(', ')
            }}
          >
            Ready to Start Coding?
          </h2>
          <p 
            className="text-xl mb-8"
            style={{ 
              color: theme.colors.text.secondary,
              fontFamily: theme.typography.fontFamily.sans.join(', ')
            }}
          >
            Join thousands of developers practicing and improving their coding skills
          </p>
          
          <button
            onClick={handleStartCoding}
            className="px-8 py-4 text-lg font-semibold rounded-lg transition-all transform hover:scale-105 active:scale-95"
            style={{
              backgroundColor: theme.colors.accent.green,
              color: theme.colors.text.inverse,
              fontFamily: theme.typography.fontFamily.sans.join(', ')
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#15803d';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = theme.colors.accent.green;
            }}
          >
            Start Coding Now
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer 
        id="footer"
        className={`border-t py-12 transition-all duration-1000 transform ${
          visibleElements.footer ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
        style={{ 
          backgroundColor: theme.colors.bg.secondary,
          borderColor: theme.colors.border.primary 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <div 
                className="w-6 h-6 rounded-md flex items-center justify-center"
                style={{ backgroundColor: theme.colors.accent.blue }}
              >
                <span 
                  className="text-white font-bold text-sm"
                  style={{ fontFamily: theme.typography.fontFamily.sans.join(', ') }}
                >
                  CM
                </span>
              </div>
              <span 
                className="text-sm"
                style={{ color: theme.colors.text.secondary }}
              >
                © 2026 CodeMate. All rights reserved.
              </span>
            </div>
            
            <div className="flex space-x-6">
              <button 
                className="text-sm transition-colors hover:text-blue-400"
                style={{ color: theme.colors.text.secondary }}
                onClick={() => window.open('#', '_blank')}
              >
                Privacy
              </button>
              <button 
                className="text-sm transition-colors hover:text-blue-400"
                style={{ color: theme.colors.text.secondary }}
                onClick={() => window.open('#', '_blank')}
              >
                Terms
              </button>
              <button 
                className="text-sm transition-colors hover:text-blue-400"
                style={{ color: theme.colors.text.secondary }}
                onClick={() => window.open('#', '_blank')}
              >
                Docs
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
